import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  cn,
  Modal,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
} from '@playerbase/ui';
import type { Playtest } from '@playerbase/types';

import {
  archivePlaytest,
  deletePlaytest,
  unarchivePlaytest,
} from '../../../games/playtests-data.js';
import { usePlaytestsRealtime } from '../../../games/use-playtests-realtime.js';
import { DisableConfirmModal } from './builder/disable-confirm-modal.js';

interface PlaytestRowProps {
  playtest: Playtest;
}

const STATUS_TONE: Record<Playtest['status'], 'neutral' | 'success' | 'warning'> = {
  draft: 'neutral',
  active: 'success',
  disabled: 'warning',
};

const STATUS_LABEL: Record<Playtest['status'], string> = {
  draft: 'Draft',
  active: 'Active',
  disabled: 'Disabled',
};

function relativeTime(iso: string): string {
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

export function PlaytestRow({ playtest }: PlaytestRowProps): JSX.Element {
  const navigate = useNavigate();
  const params = useParams<{ studioShortId: string; gameShortId: string }>();
  const { bumpPlaytestsVersion } = usePlaytestsRealtime();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [archiveInFlight, setArchiveInFlight] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [disableOpen, setDisableOpen] = useState(false);

  const archived = playtest.archivedAt !== null;
  const canDelete = playtest.status !== 'active';
  const canDisable = playtest.status === 'active';
  const deleteBlockReason =
    playtest.status === 'active'
      ? 'Disable the playtest before deleting it.'
      : null;

  function openBuilder(): void {
    if (params.studioShortId === undefined || params.gameShortId === undefined) return;
    navigate(
      `/s/${params.studioShortId}/g/${params.gameShortId}/playtest-settings/flows/${playtest.id}`,
    );
  }

  async function handleDelete(): Promise<void> {
    setBusy(true);
    setDeleteError(null);
    const result = await deletePlaytest(playtest.id);
    if (!result.ok) {
      setBusy(false);
      setDeleteError(
        result.error === 'playtest_has_sessions'
          ? 'This playtest has session data and cannot be deleted.'
          : result.error === 'playtest_active_cannot_delete'
            ? 'Disable the playtest before deleting it.'
            : 'Could not delete the playtest. Please try again.',
      );
      return;
    }
    bumpPlaytestsVersion();
    setBusy(false);
    setConfirmOpen(false);
  }

  async function handleArchive(): Promise<void> {
    setArchiveInFlight(true);
    setArchiveError(null);
    const result = await archivePlaytest(playtest.id);
    setArchiveInFlight(false);
    if (!result.ok) {
      setArchiveError('Could not archive the playtest. Please try again.');
      return;
    }
    bumpPlaytestsVersion();
  }

  async function handleUnarchive(): Promise<void> {
    setArchiveInFlight(true);
    setArchiveError(null);
    const result = await unarchivePlaytest(playtest.id);
    setArchiveInFlight(false);
    if (!result.ok) {
      setArchiveError('Could not unarchive the playtest. Please try again.');
      return;
    }
    bumpPlaytestsVersion();
  }

  const displayName = playtest.name === '' ? 'Untitled playtest' : playtest.name;

  return (
    <li>
      {/* Row container mirrors FormRow: title-area <button> + kebab
          <button> as siblings, never nested, so the click surfaces stay
          unambiguous. */}
      <div
        className={cn(
          'flex items-center justify-between gap-4 px-4 py-3 transition-colors hover:bg-surface-3',
          archived && 'opacity-60',
        )}
      >
        <button
          type="button"
          onClick={openBuilder}
          className="-mx-2 -my-1 flex min-w-0 flex-1 flex-col gap-1 rounded-md px-2 py-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <span className="flex items-center gap-2">
            <span className="truncate font-medium text-text">{displayName}</span>
            <Badge
              tone={STATUS_TONE[playtest.status]}
              aria-label={`Status: ${STATUS_LABEL[playtest.status]}`}
            >
              {STATUS_LABEL[playtest.status]}
            </Badge>
            {archived ? <Badge>Archived</Badge> : null}
          </span>
          {playtest.internalDescription !== null ? (
            <span className="truncate text-xs text-text-muted">
              {playtest.internalDescription}
            </span>
          ) : null}
        </button>

        <span className="hidden text-xs text-text-muted sm:inline">
          Updated {relativeTime(playtest.updatedAt)}
        </span>

        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Playtest actions"
              className="rounded-md px-2 py-1 text-text-muted hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              ⋯
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="min-w-[10rem]">
            {canDisable && !archived ? (
              <button
                type="button"
                onClick={() => setDisableOpen(true)}
                className="block w-full rounded-sm px-3 py-2 text-left text-sm text-text hover:bg-surface-3"
              >
                Disable playtest
              </button>
            ) : null}
            {archived ? (
              <button
                type="button"
                disabled={archiveInFlight}
                onClick={() => void handleUnarchive()}
                className="block w-full rounded-sm px-3 py-2 text-left text-sm text-text hover:bg-surface-3 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {archiveInFlight ? 'Unarchiving…' : 'Unarchive playtest'}
              </button>
            ) : (
              <button
                type="button"
                disabled={archiveInFlight}
                onClick={() => void handleArchive()}
                className="block w-full rounded-sm px-3 py-2 text-left text-sm text-text hover:bg-surface-3 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {archiveInFlight ? 'Archiving…' : 'Archive playtest'}
              </button>
            )}
            {archiveError !== null ? (
              <p className="px-3 pb-2 text-xs text-danger-text">{archiveError}</p>
            ) : null}
            <Tooltip content={deleteBlockReason} side="bottom" className="w-full">
              <button
                type="button"
                disabled={!canDelete}
                onClick={() => {
                  setDeleteError(null);
                  setConfirmOpen(true);
                }}
                className="block w-full rounded-sm px-3 py-2 text-left text-sm text-danger-text hover:bg-surface-3 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Delete playtest
              </button>
            </Tooltip>
          </PopoverContent>
        </Popover>
      </div>

      <DisableConfirmModal
        open={disableOpen}
        playtest={playtest}
        onClose={() => setDisableOpen(false)}
        onDisabled={() => {
          setDisableOpen(false);
          bumpPlaytestsVersion();
        }}
      />

      <Modal
        open={confirmOpen}
        onClose={() => {
          if (!busy) setConfirmOpen(false);
        }}
        labelledById={`delete-playtest-${playtest.id}`}
        busy={busy}
        size="sm"
      >
        <Card>
          <Card.Header>
            <Card.Title id={`delete-playtest-${playtest.id}`}>
              Delete playtest
            </Card.Title>
            <Card.Description>
              Permanently delete <strong>{displayName}</strong>? This cannot
              be undone. Sessions and responses are preserved separately.
            </Card.Description>
          </Card.Header>
          {deleteError !== null ? (
            <Card.Content>
              <p className="text-sm text-danger-text">{deleteError}</p>
            </Card.Content>
          ) : null}
          <Card.Footer>
            <Button
              variant="ghost"
              onClick={() => setConfirmOpen(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => void handleDelete()}
              disabled={busy}
            >
              {busy ? 'Deleting...' : 'Delete'}
            </Button>
          </Card.Footer>
        </Card>
      </Modal>
    </li>
  );
}
